import React, { useState } from 'react';
import { useApp } from '../../context/SupabaseAppContext';
import { isEnterpriseEmail, canUseBackdoorAccess } from '../../lib/authRouter';

// Email whitelist check (matches the one in SupabaseAppContext)
const ALLOWED_EMAILS = [
  // External emails
  'cfrancosky@gmail.com',
  'cfrancosky@outlook.com',
  'chris0f0@hotmail.com',
  'info@synthshare.ai',
  
  // Kajeet team members
  'aradhakrishnan@kajeet.com',
  'aschaible@kajeet.com',
  'areddy@kajeet.com',
  'bpeters@kajeet.com',
  'cfrancosky@kajeet.com',
  'dmech@kajeet.com',
  'dmiller@kajeet.com',
  'dpinto@kajeet.com',
  'drittenhouse@kajeet.com',
  'gabramovitz@kajeet.com',
  'gjones@kajeet.com',
  'gnoakes@kajeet.com',
  'gzukowski@kajeet.com',
  'jason@kajeet.com',
  'ksnead@kajeet.com',
  'lgarner@kajeet.com',
  'mcaplan@kajeet.com',
  'mmitchell@kajeet.com',
  'mprasad@kajeet.com',
  'msim@kajeet.com',
  'radams@kajeet.com',
  'tlehn@kajeet.com',
  'tgregor@kajeet.com',
];

const isEmailAllowed = (email: string): boolean => {
  return ALLOWED_EMAILS.includes(email.toLowerCase().trim());
};

interface AuthScreenProps {
  onAuthSuccess?: () => void;
}

const AuthScreen: React.FC<AuthScreenProps> = ({ onAuthSuccess }) => {
  const { actions } = useApp();
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [useQuickAccess, setUseQuickAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const enterpriseEmail = isEnterpriseEmail(email);

      if (useQuickAccess && password) {
        // Express Mode: Use password authentication (works for both signin and signup)
        await actions.signIn(email, password);
        setMessage('Signing you in...');
      } else if (enterpriseEmail && !useQuickAccess) {
        // Azure SSO for enterprise emails
        setMessage('Redirecting to Microsoft SSO...');
        await actions.signInWithOAuth('azure');
      } else if (isSignup) {
        // Standard signup for non-enterprise users
        if (enterpriseEmail) {
          throw new Error('Enterprise employees should use "Sign In with Microsoft" button below or Express Mode with team password.');
        }
        await actions.signUp(email, name);
        setMessage('Welcome! Please check your email for a magic link to complete your signup.');
      } else {
        // Standard signin for non-enterprise users
        if (enterpriseEmail) {
          throw new Error('Enterprise employees should use "Sign In with Microsoft" button below or Express Mode with team password.');
        }
        await actions.signIn(email);
        setMessage('Check your email for a magic link to sign in!');
      }
    } catch (error: any) {
      setError(error.message || 'An error occurred during authentication');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto mb-6">
            <img 
              src="/logo.jpg" 
              alt="Idea Hopper Logo" 
              className="h-[120px] w-auto object-contain mx-auto"
            />
          </div>
          <p className="text-lg text-gray-700 font-medium">
            {isSignup ? 'Create your account to get started' : 'Sign in to your account'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-2xl p-8" style={{ boxShadow: '0 -8px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
          <form className="space-y-6" onSubmit={handleAuth}>
            {isSignup && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required={isSignup}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-deep-teal focus:border-deep-teal"
                  placeholder="Enter your full name"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-deep-teal focus:border-deep-teal"
                placeholder="Enter your email"
              />
            </div>

            {/* Azure SSO Button - Only for enterprise emails when not using Quick Access */}
            {email && isEnterpriseEmail(email) && !useQuickAccess && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={async () => {
                    setLoading(true);
                    setError('');
                    setMessage('Redirecting to Microsoft SSO...');
                    try {
                      await actions.signInWithOAuth('azure');
                    } catch (error: any) {
                      setError(error.message || 'Microsoft SSO authentication failed. Try Express Mode below.');
                      setMessage('');
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full flex justify-center py-3 px-4 border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 rounded-md font-medium transition-colors"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2L3 7v11a2 2 0 002 2h10a2 2 0 002-2V7l-7-5z"/>
                        <path d="m9 21 0-8 2 0 0 8"/>
                      </svg>
                      🏢 Sign In with Microsoft
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Enterprise SSO for {email.split('@')[1]} employees
                </p>
              </div>
            )}

            {/* Show Quick Access toggle for whitelisted emails (both signin and signup) */}
            {email && isEmailAllowed(email) && (
              <div className="mb-4">
                <label className="flex items-center">
                  <input 
                    type="checkbox" 
                    checked={useQuickAccess}
                    onChange={(e) => {
                      setUseQuickAccess(e.target.checked);
                      if (!e.target.checked) {
                        setPassword(''); // Clear password when unchecked
                      }
                    }}
                    className="h-4 w-4 text-deep-teal focus:ring-deep-teal border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-deep-teal font-medium">
                    🚀 Quick Access (skip email verification)
                  </span>
                </label>
              </div>
            )}

            {/* Password field appears when Quick Access is checked */}
            {useQuickAccess && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Team Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required={useQuickAccess}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-deep-teal focus:border-deep-teal"
                  placeholder="Enter team password"
                />
              </div>
            )}

            <div>
              <button
                type="submit"
                                  disabled={
                    loading || 
                    (!isSignup && !email) || 
                    (isSignup && (!email || !name)) ||
                    (useQuickAccess && !password)
                  }
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-deep-teal hover:bg-dark-emerald focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deep-teal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  isSignup 
                    ? 'Create Account' 
                    : useQuickAccess 
                      ? '🚀 Sign In Instantly' 
                      : 'Send Magic Link'
                )}
              </button>
            </div>

            {message && (
              <div className="text-center p-3 bg-green-50 border border-green-200 rounded-md">
                <p className="text-sm text-green-700">{message}</p>
              </div>
            )}

            {error && (
              <div className="text-center p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsSignup(!isSignup)}
                className="text-sm text-deep-teal hover:text-dark-emerald"
              >
                {isSignup ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-xs text-gray-500 text-center space-y-1">
              {useQuickAccess ? (
                <>
                  <p>🚀 Express Mode - instant access for team members</p>
                  <p>🔒 Creates account automatically with team password</p>
                </>
              ) : email && isEnterpriseEmail(email) ? (
                <>
                  <p>🏢 Microsoft SSO - recommended for {email.split('@')[1]} employees</p>
                  <p>🚀 Or use Express Mode (Quick Access) with team password</p>
                  <p>🔒 Enterprise SSO powered by WorkOS</p>
                </>
              ) : (
                <>
                  <p>📧 Magic Link authentication for external users</p>
                  <p>🚀 Use Express Mode (Quick Access) if you have team password</p>
                  <p>🏢 Enterprise employees: Enter your work email above</p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthScreen; 